import { useState } from 'react';
import { DocumentIcon } from '@heroicons/react/24/outline';
import type { DmMessageAttachment as Attachment } from '../../../../services/dmMessages';
import {
    getAttachmentKind,
    getAttachmentTypeLabel,
    type DmAttachmentKind
} from './dmAttachmentKind';

interface DmMessageAttachmentProps {
    attachment: Attachment;
    senderName: string;
}

interface AttachmentViewProps {
    attachment: Attachment;
    altText: string;
    onLoadError: () => void;
}

const NEW_TAB_PROPS = { target: '_blank', rel: 'noopener noreferrer' } as const;
// Fixed boxes so the thread doesn't jump once media loads after the scroll-to-bottom.
const MEDIA_BOX = 'block w-60 max-w-full rounded-lg';

function ImageAttachment({ attachment, altText, onLoadError }: AttachmentViewProps) {
    return (
        <a href={attachment.url} {...NEW_TAB_PROPS} className="block w-60 max-w-full">
            <img
                src={attachment.previewUrl ?? attachment.url}
                alt={altText}
                loading="lazy"
                onError={onLoadError}
                className={`${MEDIA_BOX} aspect-[4/3] object-cover bg-black/5`}
            />
        </a>
    );
}

function VideoAttachment({ attachment, onLoadError }: AttachmentViewProps) {
    return (
        <video
            controls
            preload="metadata"
            src={attachment.url}
            onError={onLoadError}
            className={`${MEDIA_BOX} aspect-video bg-black`}
        />
    );
}

function AudioAttachment({ attachment, onLoadError }: AttachmentViewProps) {
    return (
        <audio
            controls
            src={attachment.url}
            onError={onLoadError}
            className="block w-60 max-w-full"
        />
    );
}

function FileAttachment({ attachment }: Pick<AttachmentViewProps, 'attachment'>) {
    return (
        <a
            href={attachment.url}
            {...NEW_TAB_PROPS}
            className="inline-flex items-center gap-2 rounded-lg bg-black/10 px-2.5 py-1.5 text-current hover:bg-black/15"
        >
            <DocumentIcon className="w-4 h-4 flex-shrink-0" />
            <span className="font-medium">Open attachment</span>
            <span className="rounded bg-black/10 px-1.5 py-0.5 text-[10px] font-semibold tracking-wide">
                {getAttachmentTypeLabel(attachment.mimeType)}
            </span>
        </a>
    );
}

const ATTACHMENT_VIEWS: Record<DmAttachmentKind, (props: AttachmentViewProps) => JSX.Element> = {
    image: ImageAttachment,
    video: VideoAttachment,
    audio: AudioAttachment,
    file: FileAttachment
};

function AttachmentBody({ attachment, senderName }: DmMessageAttachmentProps) {
    const [loadFailed, setLoadFailed] = useState(false);
    if (loadFailed) return <FileAttachment attachment={attachment} />;

    const View = ATTACHMENT_VIEWS[getAttachmentKind(attachment.mimeType)];
    return (
        <View
            attachment={attachment}
            altText={`Attachment from ${senderName || 'patient'}`}
            onLoadError={() => setLoadFailed(true)}
        />
    );
}

// Keyed on the URL: a refetch with a freshly signed URL retries media that failed under the old one.
export function DmMessageAttachment({ attachment, senderName }: DmMessageAttachmentProps) {
    return <AttachmentBody key={attachment.url} attachment={attachment} senderName={senderName} />;
}
